import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Upload, Trash2, Monitor } from 'lucide-react';
import { TeamLogo } from '../components/TeamLogo';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  const [settings, setSettings] = useState({
    logo_background_style: 'dark',
    logo_background_color: '#1F2937',
    scoreboard_bg_color: '#000000',
    scoreboard_text_color: '#FFFFFF',
    scoreboard_alignment: 'center',
    broadcaster_logo_url: null,
    broadcaster_logo_position: 'top-right',
    overlay_width: 1920,
    overlay_height: 1080,
    overlay_preset: 'fullhd'
  });

  // Sample teams for preview
  const sampleTeams = [
    {
      id: 1,
      name: 'Real Madrid',
      short_name: 'RMA',
      logo_url: `${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/images/teams/madrid.png`,
      primary_color: '#004D98'
    },
    {
      id: 2,
      name: 'FC Barcelona',
      short_name: 'FCB',
      logo_url: `${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/images/teams/barcelona.png`,
      primary_color: '#A50044'
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;
      console.log('[Settings Fetch] Fetching from:', `${apiUrl}/api/settings/broadcast`);

      const response = await fetch(`${apiUrl}/api/settings/broadcast`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      console.log('[Settings Fetch] Received data:', data);
      console.log('[Settings Fetch] broadcaster_logo_url from server:', data.broadcaster_logo_url);

      setSettings({
        logo_background_style: data.logo_background_style,
        logo_background_color: data.logo_background_color,
        scoreboard_bg_color: data.scoreboard_bg_color || '#000000',
        scoreboard_text_color: data.scoreboard_text_color || '#FFFFFF',
        scoreboard_alignment: data.scoreboard_alignment || 'center',
        broadcaster_logo_url: data.broadcaster_logo_url || null,
        broadcaster_logo_position: data.broadcaster_logo_position || 'top-right',
        overlay_width: data.overlay_width || 1920,
        overlay_height: data.overlay_height || 1080,
        overlay_preset: data.overlay_preset || 'fullhd'
      });
    } catch (error) {
      console.error('[Settings Fetch] Error:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  // Save logo settings only
  const handleSaveLogo = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/settings/broadcast`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          logo_background_style: settings.logo_background_style,
          logo_background_color: settings.logo_background_color
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save logo settings');
      }

      setMessage({ type: 'success', text: 'Logo settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving logo settings:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  // Reset logo settings only
  const handleResetLogo = () => {
    setSettings({
      ...settings,
      logo_background_style: 'dark',
      logo_background_color: '#1F2937'
    });
    setMessage({ type: 'info', text: 'Logo settings reset to default' });
    setTimeout(() => setMessage(null), 3000);
  };

  // Save scoreboard settings only
  const handleSaveScoreboard = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/settings/broadcast`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          scoreboard_bg_color: settings.scoreboard_bg_color,
          scoreboard_text_color: settings.scoreboard_text_color,
          scoreboard_alignment: settings.scoreboard_alignment
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save scoreboard settings');
      }

      setMessage({ type: 'success', text: 'Scoreboard settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving scoreboard settings:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  // Reset scoreboard settings only
  const handleResetScoreboard = () => {
    setSettings({
      ...settings,
      scoreboard_bg_color: '#000000',
      scoreboard_text_color: '#FFFFFF',
      scoreboard_alignment: 'center'
    });
    setMessage({ type: 'info', text: 'Scoreboard settings reset to default' });
    setTimeout(() => setMessage(null), 3000);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Check if it's an image
      if (file.type.startsWith('image/')) {
        handleFileUpload(file);
      } else {
        setMessage({ type: 'error', text: 'Please drop an image file (PNG, JPG, SVG)' });
        setTimeout(() => setMessage(null), 3000);
      }
    }
  };

  // Upload file (used by both click and drag & drop)
  const handleFileUpload = async (file) => {
    if (!file) return;

    console.log('[Broadcaster Logo Upload] Starting upload for file:', file.name);
    console.log('[Broadcaster Logo Upload] File size:', file.size, 'bytes');
    console.log('[Broadcaster Logo Upload] File type:', file.type);

    setUploading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      console.log('[Broadcaster Logo Upload] Token exists:', !!token);
      console.log('[Broadcaster Logo Upload] Token (first 30 chars):', token?.substring(0, 30) + '...');

      const apiUrl = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;
      const url = `${apiUrl}/api/settings/broadcaster-logo`;
      console.log('[Broadcaster Logo Upload] API URL:', apiUrl);
      console.log('[Broadcaster Logo Upload] Full URL:', url);
      console.log('[Broadcaster Logo Upload] Window location hostname:', window.location.hostname);

      const formData = new FormData();
      formData.append('logo', file);
      console.log('[Broadcaster Logo Upload] FormData created');
      console.log('[Broadcaster Logo Upload] FormData has logo:', formData.has('logo'));

      console.log('[Broadcaster Logo Upload] ===== ABOUT TO FETCH =====');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
          // DO NOT include Content-Type with FormData - browser sets it automatically
        },
        body: formData
      });

      console.log('[Broadcaster Logo Upload] ===== FETCH COMPLETED =====');
      console.log('[Broadcaster Logo Upload] Response status:', response.status);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload broadcaster logo');
      }

      const data = await response.json();
      console.log('[Broadcaster Logo Upload] Response:', data);

      // Use callback to ensure we get the latest state
      setSettings(prevSettings => ({
        ...prevSettings,
        broadcaster_logo_url: data.logoUrl
      }));

      setMessage({ type: 'success', text: 'Broadcaster logo uploaded successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('[Broadcaster Logo Upload] Error:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
    }
  };

  // Upload broadcaster logo (from file input)
  const handleBroadcasterLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    handleFileUpload(file);
  };

  // Save broadcaster logo position
  const handleSaveBroadcasterPosition = async () => {
    console.log('[Broadcaster Logo Position] Saving position:', settings.broadcaster_logo_position);
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/settings/broadcast`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          broadcaster_logo_position: settings.broadcaster_logo_position
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save logo position');
      }

      const data = await response.json();
      console.log('[Broadcaster Logo Position] Response:', data);

      setMessage({ type: 'success', text: 'Logo position saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('[Broadcaster Logo Position] Error:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  // Remove broadcaster logo
  const handleBroadcasterLogoRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the broadcaster logo?')) {
      return;
    }

    console.log('[Broadcaster Logo Remove] Starting removal');
    setUploading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/settings/broadcaster-logo`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove broadcaster logo');
      }

      const data = await response.json();
      console.log('[Broadcaster Logo Remove] Response:', data);

      // Use callback to ensure we get the latest state
      setSettings(prevSettings => ({
        ...prevSettings,
        broadcaster_logo_url: null
      }));

      setMessage({ type: 'success', text: 'Broadcaster logo removed successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('[Broadcaster Logo Remove] Error:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
    }
  };

  const logoStyle = {
    backgroundStyle: settings.logo_background_style,
    backgroundColor: settings.logo_background_color
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  // Debug: log current settings
  console.log('[Settings Render] Current settings:', settings);
  console.log('[Settings Render] Broadcaster logo URL:', settings.broadcaster_logo_url);
  console.log('[Settings Render] Broadcaster logo position:', settings.broadcaster_logo_position);

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Broadcast Settings</h1>
          <p className="text-gray-600">
            Configure how team logos are displayed in Vision Master overlays
          </p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Settings Form */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Team Logo Appearance</h2>

          {/* Background Style */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3">Background Style:</label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="style"
                  value="transparent"
                  checked={settings.logo_background_style === 'transparent'}
                  onChange={(e) => setSettings({ ...settings, logo_background_style: e.target.value })}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Transparent</div>
                  <div className="text-xs text-gray-500">No background, native logo transparency</div>
                </div>
              </label>

              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="style"
                  value="white"
                  checked={settings.logo_background_style === 'white'}
                  onChange={(e) => setSettings({ ...settings, logo_background_style: e.target.value })}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">White Circle</div>
                  <div className="text-xs text-gray-500">Clean white background, good for dark logos</div>
                </div>
              </label>

              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="style"
                  value="dark"
                  checked={settings.logo_background_style === 'dark'}
                  onChange={(e) => setSettings({ ...settings, logo_background_style: e.target.value })}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Dark Circle</div>
                  <div className="text-xs text-gray-500">Professional broadcast style (recommended)</div>
                </div>
              </label>

              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="style"
                  value="custom"
                  checked={settings.logo_background_style === 'custom'}
                  onChange={(e) => setSettings({ ...settings, logo_background_style: e.target.value })}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Custom Color</div>
                  <div className="text-xs text-gray-500">Choose your own background color</div>
                </div>
              </label>
            </div>
          </div>

          {/* Color Picker (shown only when custom selected) */}
          {settings.logo_background_style === 'custom' && (
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">Custom Background Color:</label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={settings.logo_background_color}
                  onChange={(e) => setSettings({ ...settings, logo_background_color: e.target.value })}
                  className="w-20 h-12 rounded border cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.logo_background_color}
                  onChange={(e) => setSettings({ ...settings, logo_background_color: e.target.value })}
                  className="flex-1 px-4 py-2 border rounded-lg font-mono text-sm"
                  pattern="#[0-9A-Fa-f]{6}"
                  placeholder="#1F2937"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter hex color code (e.g., #1F2937)
              </p>
            </div>
          )}

          {/* Preview */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3">Preview:</label>
            <div className="border-2 border-gray-200 rounded-lg p-8 bg-gradient-to-r from-gray-900 to-gray-800">
              <div className="flex justify-center items-center gap-8">
                {sampleTeams.map((team, index) => (
                  <div key={team.id} className="text-center">
                    <TeamLogo team={team} size="w-24 h-24" logoStyle={logoStyle} />
                    <div className="text-white text-sm font-semibold mt-2">
                      {team.short_name}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-center text-gray-400 text-xs mt-4">
                ↑ This is how logos will appear in Vision Master overlays
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleResetLogo}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
              disabled={saving}
            >
              <RotateCcw size={18} />
              Reset Logo
            </button>
            <button
              onClick={handleSaveLogo}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Logo Settings'}
            </button>
          </div>
        </div>

        {/* Scoreboard Appearance Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Scoreboard Appearance</h2>

          {/* Scoreboard Background Color */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Scoreboard Background Color:</label>
            <div className="flex gap-3 items-center">
              <input
                type="color"
                value={settings.scoreboard_bg_color}
                onChange={(e) => setSettings({ ...settings, scoreboard_bg_color: e.target.value })}
                className="w-20 h-12 rounded border cursor-pointer"
              />
              <input
                type="text"
                value={settings.scoreboard_bg_color}
                onChange={(e) => setSettings({ ...settings, scoreboard_bg_color: e.target.value })}
                className="flex-1 px-4 py-2 border rounded-lg font-mono text-sm"
                pattern="#[0-9A-Fa-f]{6}"
                placeholder="#000000"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Background color for the scoreboard overlay (e.g., #000000 for black)
            </p>
          </div>

          {/* Scoreboard Text Color */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Scoreboard Text Color:</label>
            <div className="flex gap-3 items-center">
              <input
                type="color"
                value={settings.scoreboard_text_color}
                onChange={(e) => setSettings({ ...settings, scoreboard_text_color: e.target.value })}
                className="w-20 h-12 rounded border cursor-pointer"
              />
              <input
                type="text"
                value={settings.scoreboard_text_color}
                onChange={(e) => setSettings({ ...settings, scoreboard_text_color: e.target.value })}
                className="flex-1 px-4 py-2 border rounded-lg font-mono text-sm"
                pattern="#[0-9A-Fa-f]{6}"
                placeholder="#FFFFFF"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Text color for team names, scores, and clock (e.g., #FFFFFF for white)
            </p>
          </div>

          {/* Scoreboard Alignment */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3">Scoreboard Position:</label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="alignment"
                  value="left"
                  checked={settings.scoreboard_alignment === 'left'}
                  onChange={(e) => setSettings({ ...settings, scoreboard_alignment: e.target.value })}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Left</div>
                  <div className="text-xs text-gray-500">Scoreboard aligned to the left</div>
                </div>
              </label>

              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="alignment"
                  value="center"
                  checked={settings.scoreboard_alignment === 'center'}
                  onChange={(e) => setSettings({ ...settings, scoreboard_alignment: e.target.value })}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Center</div>
                  <div className="text-xs text-gray-500">Scoreboard centered (recommended)</div>
                </div>
              </label>

              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="alignment"
                  value="right"
                  checked={settings.scoreboard_alignment === 'right'}
                  onChange={(e) => setSettings({ ...settings, scoreboard_alignment: e.target.value })}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">Right</div>
                  <div className="text-xs text-gray-500">Scoreboard aligned to the right</div>
                </div>
              </label>
            </div>
          </div>

          {/* Preview */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3">Preview:</label>
            <div
              className="border-2 border-gray-200 rounded-lg p-8"
              style={{ backgroundColor: settings.scoreboard_bg_color }}
            >
              <div className={`flex ${
                settings.scoreboard_alignment === 'left' ? 'justify-start' :
                settings.scoreboard_alignment === 'right' ? 'justify-end' :
                'justify-center'
              }`}>
                <div className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-lg">
                  <div className="flex items-center gap-6" style={{ color: settings.scoreboard_text_color }}>
                    <div className="text-center">
                      <div className="text-sm mb-1">RMA</div>
                      <div className="text-3xl font-bold">2</div>
                    </div>
                    <div className="text-2xl">-</div>
                    <div className="text-center">
                      <div className="text-sm mb-1">FCB</div>
                      <div className="text-3xl font-bold">1</div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center text-white/60 text-xs mt-4">
                ↑ Scoreboard preview with current settings
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleResetScoreboard}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
              disabled={saving}
            >
              <RotateCcw size={18} />
              Reset Scoreboard
            </button>
            <button
              onClick={handleSaveScoreboard}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Scoreboard Settings'}
            </button>
          </div>
        </div>

        {/* Broadcaster Logo Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Broadcaster Logo</h2>
          <p className="text-sm text-gray-600 mb-4">
            Upload your TV channel or broadcaster logo to display in the overlay
          </p>

          {/* Current Logo Display */}
          {settings.broadcaster_logo_url && (
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3">Current Logo:</label>
              <div className="flex items-center gap-4 p-4 bg-gray-100 rounded-lg">
                <img
                  src={settings.broadcaster_logo_url}
                  alt="Broadcaster Logo"
                  className="h-16 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <button
                  onClick={handleBroadcasterLogoRemove}
                  disabled={uploading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 flex items-center gap-2"
                >
                  <Trash2 size={18} />
                  Remove Logo
                </button>
              </div>
            </div>
          )}

          {/* Upload Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3">
              {settings.broadcaster_logo_url ? 'Replace Logo:' : 'Upload Logo:'}
            </label>
            <div
              className="flex-1 px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 cursor-pointer transition-colors bg-gray-50 hover:bg-blue-50"
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('broadcaster-logo-input').click()}
            >
              <div className="flex flex-col items-center justify-center gap-2 text-gray-600">
                <Upload size={32} />
                <span className="font-medium">{uploading ? 'Uploading...' : 'Drag & Drop or Click to Upload'}</span>
                <span className="text-sm text-gray-500">PNG, JPG, or SVG (max 500KB)</span>
              </div>
              <input
                id="broadcaster-logo-input"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                onChange={handleBroadcasterLogoUpload}
                disabled={uploading}
                className="hidden"
              />
            </div>
          </div>

          {/* Logo Position Configuration */}
          {settings.broadcaster_logo_url && (
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3">Logo Position:</label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="broadcaster_logo_position"
                    value="top-left"
                    checked={settings.broadcaster_logo_position === 'top-left'}
                    onChange={(e) => setSettings({ ...settings, broadcaster_logo_position: e.target.value })}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium">Top Left</div>
                    <div className="text-xs text-gray-500">↖ Upper left corner</div>
                  </div>
                </label>

                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="broadcaster_logo_position"
                    value="top-right"
                    checked={settings.broadcaster_logo_position === 'top-right'}
                    onChange={(e) => setSettings({ ...settings, broadcaster_logo_position: e.target.value })}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium">Top Right</div>
                    <div className="text-xs text-gray-500">↗ Upper right corner</div>
                  </div>
                </label>

                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="broadcaster_logo_position"
                    value="bottom-left"
                    checked={settings.broadcaster_logo_position === 'bottom-left'}
                    onChange={(e) => setSettings({ ...settings, broadcaster_logo_position: e.target.value })}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium">Bottom Left</div>
                    <div className="text-xs text-gray-500">↙ Lower left corner</div>
                  </div>
                </label>

                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="broadcaster_logo_position"
                    value="bottom-right"
                    checked={settings.broadcaster_logo_position === 'bottom-right'}
                    onChange={(e) => setSettings({ ...settings, broadcaster_logo_position: e.target.value })}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium">Bottom Right</div>
                    <div className="text-xs text-gray-500">↘ Lower right corner</div>
                  </div>
                </label>
              </div>

              {/* Save Position Button */}
              <div className="mt-4">
                <button
                  onClick={handleSaveBroadcasterPosition}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Logo Position'}
                </button>
              </div>
            </div>
          )}

          {/* Preview */}
          {settings.broadcaster_logo_url && (
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3">Preview in Overlay:</label>
              <div className="border-2 border-gray-200 rounded-lg p-8 bg-black relative h-64">
                <img
                  src={settings.broadcaster_logo_url}
                  alt="Broadcaster Logo Preview"
                  className={`h-12 object-contain opacity-80 absolute ${
                    settings.broadcaster_logo_position === 'top-left' ? 'top-4 left-4' :
                    settings.broadcaster_logo_position === 'top-right' ? 'top-4 right-4' :
                    settings.broadcaster_logo_position === 'bottom-left' ? 'bottom-4 left-4' :
                    'bottom-4 right-4'
                  }`}
                />
                <p className="absolute bottom-2 left-0 right-0 text-center text-gray-400 text-xs">
                  Logo preview in {settings.broadcaster_logo_position.replace('-', ' ')} position
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Overlay Resolution Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Monitor size={24} />
            Overlay Resolution
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Set the output resolution for the overlay window (OBS Browser Source / Vision Master)
          </p>

          {/* Preset Buttons */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3">Resolution Preset:</label>
            <div className="space-y-2">
              {[
                { value: 'hd', label: 'HD', desc: '1280 x 720', w: 1280, h: 720 },
                { value: 'fullhd', label: 'Full HD', desc: '1920 x 1080 (recommended)', w: 1920, h: 1080 },
                { value: '4k', label: '4K UHD', desc: '3840 x 2160', w: 3840, h: 2160 },
                { value: 'custom', label: 'Custom', desc: 'Enter custom width and height', w: null, h: null },
              ].map((preset) => (
                <label key={preset.value} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="overlay_preset"
                    value={preset.value}
                    checked={settings.overlay_preset === preset.value}
                    onChange={() => {
                      const newSettings = { ...settings, overlay_preset: preset.value };
                      if (preset.w !== null) {
                        newSettings.overlay_width = preset.w;
                        newSettings.overlay_height = preset.h;
                      }
                      setSettings(newSettings);
                    }}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium">{preset.label}</div>
                    <div className="text-xs text-gray-500">{preset.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Custom Dimensions (shown only when custom) */}
          {settings.overlay_preset === 'custom' && (
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">Custom Dimensions (px):</label>
              <div className="flex gap-3 items-center">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Width</label>
                  <input
                    type="number"
                    value={settings.overlay_width}
                    onChange={(e) => setSettings({ ...settings, overlay_width: parseInt(e.target.value) || 320 })}
                    min={320}
                    max={7680}
                    className="w-full px-4 py-2 border rounded-lg font-mono text-sm"
                    placeholder="1920"
                  />
                </div>
                <span className="text-gray-400 mt-5 font-bold">x</span>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Height</label>
                  <input
                    type="number"
                    value={settings.overlay_height}
                    onChange={(e) => setSettings({ ...settings, overlay_height: parseInt(e.target.value) || 240 })}
                    min={240}
                    max={4320}
                    className="w-full px-4 py-2 border rounded-lg font-mono text-sm"
                    placeholder="1080"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Width: 320-7680px, Height: 240-4320px
              </p>
            </div>
          )}

          {/* Current Resolution Display */}
          <div className="mb-6 p-4 bg-gray-100 rounded-lg">
            <div className="text-sm text-gray-600">
              Current overlay size: <span className="font-mono font-bold text-gray-900">{settings.overlay_width} x {settings.overlay_height}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                setSettings({
                  ...settings,
                  overlay_width: 1920,
                  overlay_height: 1080,
                  overlay_preset: 'fullhd'
                });
                setMessage({ type: 'info', text: 'Overlay resolution reset to Full HD' });
                setTimeout(() => setMessage(null), 3000);
              }}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
              disabled={saving}
            >
              <RotateCcw size={18} />
              Reset Resolution
            </button>
            <button
              onClick={async () => {
                setSaving(true);
                setMessage(null);
                try {
                  const token = localStorage.getItem('token');
                  const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/settings/broadcast`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      overlay_width: settings.overlay_width,
                      overlay_height: settings.overlay_height,
                      overlay_preset: settings.overlay_preset
                    })
                  });
                  if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.error || 'Failed to save overlay resolution');
                  }
                  setMessage({ type: 'success', text: 'Overlay resolution saved successfully!' });
                  setTimeout(() => setMessage(null), 3000);
                } catch (error) {
                  console.error('Error saving overlay resolution:', error);
                  setMessage({ type: 'error', text: error.message });
                } finally {
                  setSaving(false);
                }
              }}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Resolution'}
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">ℹ️ How it works</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Settings apply to all Vision Master overlays (Scoreboard, Graphics, etc.)</li>
            <li>• Changes take effect immediately when overlays reload</li>
            <li>• Logos in operator views (LiveMatchView) always use dark background</li>
            <li>• If logo fails to load, team initials are shown as fallback</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Settings;
